import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Globe, Key, FileText, Activity } from 'lucide-react';
import SEO from '@/components/SEO';

const AddOns = () => {
  const trustCategories = [
    { icon: Shield, title: 'SSL/TLS health', desc: 'Certificate validity, expiration, configuration. Alerts before expiry; remediation steps.' },
    { icon: Mail, title: 'Email authentication', desc: 'SPF, DKIM, DMARC configuration and validity. Pass/fail status and fix instructions.' },
    { icon: Globe, title: 'Domain reputation', desc: 'Blacklist status, spam reputation, phishing monitoring. Alerts and delisting guidance.' },
    { icon: Key, title: 'Credential breach exposure', desc: 'Domain in breach databases. Privacy-preserving alerts and remediation workflow.' },
    { icon: FileText, title: 'Certificate transparency', desc: 'Certificates issued for your domains. Unauthorized issuance alerts.' },
    { icon: Activity, title: 'Service uptime', desc: 'Endpoint availability and response times. Multi-region checks and historical reporting.' },
  ];

  return (
    <div>
      <SEO
        title="Security Add-Ons - Trust Monitoring & More"
        description="Enhance your IAM implementation with Trust Monitoring add-ons including SSL/TLS health, email authentication, domain reputation, and credential breach monitoring."
        keywords="trust monitoring add-ons, SSL monitoring, domain reputation monitoring, credential breach detection, email authentication"
        ogTitle="Security Add-Ons - Trust Monitoring & More"
        ogDescription="Enhance your IAM implementation with Trust Monitoring add-ons including SSL/TLS health, email authentication, domain reputation, and credential breach monitoring."
        canonicalUrl="https://idnty.one/add-ons"
      />
      {/* Hero */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="hero-title" style={{ marginBottom: '1.5rem' }}>
            ADD-ONS
          </h1>
          <p style={{ fontSize: '1.2rem', maxWidth: '640px', margin: '0 auto 2rem', lineHeight: '1.7' }}>
            Trust Monitoring and other security add-ons complement your IAM engagement. Included with both IAM tiers; scope varies by tier.
          </p>
          <Link to="/" className="btn btn-secondary" style={{ marginRight: '0.75rem' }}>
            Back to IAM
          </Link>
          <Link to="/contact" className="btn">
            Schedule Consultation
          </Link>
        </div>
      </section>

      {/* Trust Monitoring — main add-on */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            TRUST MONITORING
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '2.5rem', fontSize: '1rem', maxWidth: '700px', margin: '0 auto 2.5rem' }}>
            Continuous verification of the signals customers and partners check: SSL/TLS, email authentication, domain reputation, and breach exposure.
          </p>

          <div className="grid-2" style={{ marginBottom: '3rem' }}>
            <div className="card-heavy" style={{ background: 'var(--white)' }}>
              <div className="label" style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>With Essential</div>
              <h3 className="feature-heading" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Up to 3 domains</h3>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                All Trust Monitoring checks (SSL, email auth, domain reputation, breach, CT, uptime) for up to 3 domains. Included in your Essential IAM engagement.
              </p>
            </div>
            <div className="card-heavy" style={{ background: 'var(--gold)' }}>
              <div className="label" style={{ marginBottom: '0.5rem', color: 'var(--black)' }}>With Professional</div>
              <h3 className="feature-heading" style={{ fontSize: '1.25rem', marginBottom: '1rem', color: 'var(--black)' }}>Up to 10 domains</h3>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: 'var(--black)' }}>
                Extended domain coverage. Included in your Professional IAM engagement.
              </p>
            </div>
          </div>

          <h3 className="feature-heading" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            WHAT WE MONITOR
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {trustCategories.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  style={{
                    border: '2px solid var(--gray)',
                    padding: '1.5rem',
                    background: 'var(--white)',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-start',
                  }}
                >
                  <Icon size={32} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <div>
                    <div className="label" style={{ marginBottom: '0.35rem' }}>{item.title}</div>
                    <p style={{ fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other add-ons placeholder */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            OTHER SECURITY ADD-ONS
          </h2>
          <p style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto', fontSize: '0.95rem', color: 'var(--gray)' }}>
            Additional security and compliance add-ons can be scoped with your engagement. Ask during your consultation.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--black)', color: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
            Add-ons are included with IAM tiers. Start with identity — we'll align add-ons to your tier.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/pricing" className="btn btn-gold">
              View IAM Tiers
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Contact
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddOns;
